// La info de una imagen del lienzo, armada al abrir su panel de Info —
// ADR-2026-09-16-la-card-de-info-del-lienzo.
//
// Lo que ya está en la fila (`infoDeImagen.ts`) sale en el acto. Lo que no, se busca
// recién al abrir y se cachea por imagen para toda la sesión:
//  • el PESO, que no se guarda en ningún lado: metadata del objeto en el storage
//    (`list` con `search`, el mismo camino que `listarImagenesSubidas`);
//  • la RESOLUCIÓN cuando la fila no trae `ancho`/`alto` (filas viejas, del editor):
//    se carga el original firmado y se mide;
//  • los catálogos de FONDOS (guardados y biblioteca), que el chat no tiene a mano:
//    una vez por empresa, y sólo si la fila pidió un fondo.

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchBibliotecaFondos, fetchFondosGuardados, type FondoBiblioteca, type FondoGuardado } from '../../pages/v2/fondosApi';
import { firmarEnLote } from '../../utils/firmarEnLote';
import { useEntornoChatAtlas } from '../entornoContext';
import type { CeldaParaAccion } from './accionesDeCelda';
import { fondoPedido, resolucionDeFila, resolverInfo, type InfoDeImagen } from './infoDeImagen';

const pesos = new Map<string, number | null>();
const resoluciones = new Map<string, { ancho: number; alto: number } | null>();
const fondosPorEmpresa = new Map<
  string,
  Promise<{ guardados: FondoGuardado[]; biblioteca: FondoBiblioteca[] }>
>();

async function pesoDelOriginal(companyId: string, fileName: string): Promise<number | null> {
  const { data, error } = await supabase.storage
    .from('generadas')
    .list(`${companyId}/full`, { search: fileName, limit: 1 });
  if (error || !data?.[0]) return null;
  const size = (data[0].metadata as { size?: unknown } | null)?.size;
  return typeof size === 'number' && size > 0 ? size : null;
}

async function medirOriginal(
  companyId: string,
  fileName: string,
): Promise<{ ancho: number; alto: number } | null> {
  const path = `${companyId}/full/${fileName}`;
  const url = (await firmarEnLote([path])).get(path);
  if (!url) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve(img.naturalWidth > 0 ? { ancho: img.naturalWidth, alto: img.naturalHeight } : null);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function catalogosDeFondos(companyId: string) {
  let p = fondosPorEmpresa.get(companyId);
  if (!p) {
    p = Promise.all([fetchFondosGuardados(companyId), fetchBibliotecaFondos(null)])
      .then(([guardados, biblioteca]) => ({ guardados, biblioteca }))
      .catch(() => ({ guardados: [], biblioteca: [] }));
    fondosPorEmpresa.set(companyId, p);
  }
  return p;
}

export interface InfoResuelta {
  info: InfoDeImagen;
  /** Bytes del original; `null` = no se pudo saber; `undefined` = todavía buscando. */
  peso: number | null | undefined;
}

export function useInfoDeImagen(celda: CeldaParaAccion | null, abierta: boolean): InfoResuelta | null {
  const { productos, modelos } = useEntornoChatAtlas();
  const [fondos, setFondos] = useState<{ guardados: FondoGuardado[]; biblioteca: FondoBiblioteca[] }>({
    guardados: [],
    biblioteca: [],
  });
  const [peso, setPeso] = useState<number | null | undefined>(undefined);
  const [medida, setMedida] = useState<{ ancho: number; alto: number } | null>(null);
  const imageId = celda?.imageId ?? null;
  const fileName = celda?.datos.fileName ?? null;
  const companyId = celda?.companyId ?? null;
  const pideFondo = celda ? fondoPedido(celda.datos) !== null : false;
  const traeResolucion = celda ? resolucionDeFila(celda.datos) !== null : true;

  useEffect(() => {
    if (!abierta || !imageId || !fileName || !companyId) return;
    let vivo = true;
    // Peso.
    if (pesos.has(imageId)) setPeso(pesos.get(imageId));
    else {
      setPeso(undefined);
      void pesoDelOriginal(companyId, fileName).then((b) => {
        pesos.set(imageId, b);
        if (vivo) setPeso(b);
      });
    }
    // Resolución medida, sólo si la fila no la trae.
    if (!traeResolucion) {
      if (resoluciones.has(imageId)) setMedida(resoluciones.get(imageId) ?? null);
      else {
        void medirOriginal(companyId, fileName).then((r) => {
          resoluciones.set(imageId, r);
          if (vivo) setMedida(r);
        });
      }
    }
    // Fondos, sólo si hace falta.
    if (pideFondo) {
      void catalogosDeFondos(companyId).then((f) => {
        if (vivo) setFondos(f);
      });
    }
    return () => {
      vivo = false;
    };
  }, [abierta, imageId, fileName, companyId, pideFondo, traeResolucion]);

  return useMemo(() => {
    if (!celda) return null;
    const info = resolverInfo(celda.datos, {
      productos,
      modelos,
      fondosGuardados: fondos.guardados,
      fondosBiblioteca: fondos.biblioteca,
    });
    return { info: { ...info, resolucion: info.resolucion ?? medida }, peso };
  }, [celda, productos, modelos, fondos, medida, peso]);
}
